import React from 'react';
import { UserProgress, Language } from '../types';
import { Trophy, Zap, Target, TrendingUp, Award, Lock, Sparkles, Brain, Telescope, Heart } from 'lucide-react';

interface ProgressDashboardProps {
  progress: UserProgress | null;
  language: Language;
  compact?: boolean;
}

const AI_TIER_INFO: Record<string, { icon: React.ReactNode; color: string; gradient: string }> = {
  explorer: {
    icon: <Telescope size={20} />,
    color: 'text-gray-700',
    gradient: 'from-gray-600 to-slate-600'
  },
  learner: {
    icon: <Brain size={20} />,
    color: 'text-blue-700',
    gradient: 'from-blue-600 to-indigo-600'
  },
  companion: {
    icon: <Heart size={20} />,
    color: 'text-purple-700',
    gradient: 'from-purple-600 to-pink-600'
  },
  oracle: {
    icon: <Sparkles size={20} />,
    color: 'text-amber-700',
    gradient: 'from-amber-500 to-orange-500'
  }
};

const TRANSLATIONS: Record<Language, {
  aiTier: string;
  progress: string;
  milestones: string;
  achievements: string;
  nextGoal: string;
  currentStreak: string;
  days: string;
  unlocked: string;
  locked: string;
  tierNames: Record<string, string>;
  tierDescriptions: Record<string, string>;
}> = {
  Italian: {
    aiTier: 'Livello AI',
    progress: 'Progresso',
    milestones: 'Traguardi',
    achievements: 'Conquiste',
    nextGoal: 'Prossimo obiettivo',
    currentStreak: 'Serie attuale',
    days: 'giorni',
    unlocked: 'Sbloccato',
    locked: 'Bloccato',
    tierNames: {
      explorer: 'Esploratore',
      learner: 'Studente',
      companion: 'Compagno',
      oracle: 'Oracolo'
    },
    tierDescriptions: {
      explorer: 'Sto imparando a conoscerti',
      learner: 'Inizio a vedere i pattern',
      companion: 'Ti conosco bene',
      oracle: 'Conosco profondamente il tuo percorso'
    }
  },
  English: {
    aiTier: 'AI Level',
    progress: 'Progress',
    milestones: 'Milestones',
    achievements: 'Achievements',
    nextGoal: 'Next goal',
    currentStreak: 'Current streak',
    days: 'days',
    unlocked: 'Unlocked',
    locked: 'Locked',
    tierNames: {
      explorer: 'Explorer',
      learner: 'Learner',
      companion: 'Companion',
      oracle: 'Oracle'
    },
    tierDescriptions: {
      explorer: 'Getting to know you',
      learner: 'Starting to see patterns',
      companion: 'I know you well',
      oracle: 'Deep understanding of your journey'
    }
  },
  Spanish: {
    aiTier: 'Nivel IA',
    progress: 'Progreso',
    milestones: 'Hitos',
    achievements: 'Logros',
    nextGoal: 'Próximo objetivo',
    currentStreak: 'Racha actual',
    days: 'días',
    unlocked: 'Desbloqueado',
    locked: 'Bloqueado',
    tierNames: {
      explorer: 'Explorador',
      learner: 'Aprendiz',
      companion: 'Compañero',
      oracle: 'Oráculo'
    },
    tierDescriptions: {
      explorer: 'Conociéndote',
      learner: 'Empiezo a ver patrones',
      companion: 'Te conozco bien',
      oracle: 'Comprensión profunda de tu viaje'
    }
  },
  German: {
    aiTier: 'KI-Stufe',
    progress: 'Fortschritt',
    milestones: 'Meilensteine',
    achievements: 'Erfolge',
    nextGoal: 'Nächstes Ziel',
    currentStreak: 'Aktuelle Serie',
    days: 'Tage',
    unlocked: 'Freigeschaltet',
    locked: 'Gesperrt',
    tierNames: {
      explorer: 'Entdecker',
      learner: 'Lerner',
      companion: 'Begleiter',
      oracle: 'Orakel'
    },
    tierDescriptions: {
      explorer: 'Lerne dich kennen',
      learner: 'Beginne Muster zu sehen',
      companion: 'Kenne dich gut',
      oracle: 'Tiefes Verständnis deiner Reise'
    }
  },
  French: {
    aiTier: 'Niveau IA',
    progress: 'Progrès',
    milestones: 'Jalons',
    achievements: 'Réalisations',
    nextGoal: 'Prochain objectif',
    currentStreak: 'Série actuelle',
    days: 'jours',
    unlocked: 'Débloqué',
    locked: 'Verrouillé',
    tierNames: {
      explorer: 'Explorateur',
      learner: 'Apprenant',
      companion: 'Compagnon',
      oracle: 'Oracle'
    },
    tierDescriptions: {
      explorer: 'Apprendre à te connaître',
      learner: 'Commence à voir des motifs',
      companion: 'Je te connais bien',
      oracle: 'Compréhension profonde de ton parcours'
    }
  }
};

const ProgressDashboard: React.FC<ProgressDashboardProps> = ({ progress, language, compact = false }) => {
  if (!progress) return null;

  const t = TRANSLATIONS[language] || TRANSLATIONS.English;
  const tierInfo = AI_TIER_INFO[progress.aiTier];
  const unlockedMilestones = progress.milestones.filter(m => m.isUnlocked);
  const nextMilestone = progress.nextMilestone;

  if (compact) {
    return (
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-4 border border-indigo-100">
        {/* AI Tier Badge */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${tierInfo.gradient} flex items-center justify-center text-white shadow-lg`}>
              {tierInfo.icon}
            </div>
            <div>
              <p className="text-xs font-bold text-black/40 uppercase tracking-wider">{t.aiTier}</p>
              <p className={`text-sm font-black ${tierInfo.color}`}>
                {t.tierNames[progress.aiTier]}
              </p>
            </div>
          </div>
          {progress.currentStreak > 0 && (
            <div className="flex items-center gap-1.5 bg-orange-100 px-3 py-1.5 rounded-lg">
              <Zap size={14} className="text-orange-600" />
              <span className="text-xs font-black text-orange-700">
                {progress.currentStreak} {t.days}
              </span>
            </div>
          )}
        </div>

        {/* Next Milestone */}
        {nextMilestone && (
          <div className="bg-white/60 rounded-xl p-3 border border-white/40">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-black/60">{t.nextGoal}</p>
              <span className="text-xs font-black text-indigo-600">
                {nextMilestone.currentProgress}/{nextMilestone.threshold}
              </span>
            </div>
            <div className="w-full h-2 bg-black/5 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 transition-all duration-500"
                style={{ width: `${(nextMilestone.currentProgress / nextMilestone.threshold) * 100}%` }}
              />
            </div>
            <p className="text-xs font-medium text-black/70 mt-2">{nextMilestone.title}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* AI Tier Card */}
      <div className={`bg-gradient-to-br ${tierInfo.gradient} rounded-2xl p-6 text-white shadow-xl`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              {tierInfo.icon}
            </div>
            <div>
              <p className="text-xs font-bold text-white/60 uppercase tracking-wider">{t.aiTier}</p>
              <p className="text-xl font-black">{t.tierNames[progress.aiTier]}</p>
            </div>
          </div>
          {progress.currentStreak > 0 && (
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-2 rounded-lg">
              <Zap size={16} className="text-white" />
              <div>
                <p className="text-xs font-medium text-white/80">{t.currentStreak}</p>
                <p className="text-sm font-black">{progress.currentStreak} {t.days}</p>
              </div>
            </div>
          )}
        </div>
        <p className="text-sm text-white/80 font-medium">{t.tierDescriptions[progress.aiTier]}</p>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <p className="text-2xl font-black">{progress.totalEntries}</p>
            <p className="text-xs text-white/70 font-medium">Moments</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <p className="text-2xl font-black">{progress.totalPatterns}</p>
            <p className="text-xs text-white/70 font-medium">Patterns</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <p className="text-2xl font-black">{progress.totalDecisions}</p>
            <p className="text-xs text-white/70 font-medium">Decisions</p>
          </div>
        </div>
      </div>

      {/* Milestones */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Target size={18} className="text-indigo-600" />
          <h3 className="font-black text-sm text-black">{t.milestones}</h3>
          <span className="ml-auto text-xs font-bold text-black/40">
            {unlockedMilestones.length}/{progress.milestones.length}
          </span>
        </div>
        <div className="space-y-3">
          {progress.milestones.slice(0, compact ? 3 : undefined).map((milestone) => (
            <div key={milestone.id} className={`p-3 rounded-xl border transition-all ${
              milestone.isUnlocked
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
                : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {milestone.isUnlocked ? (
                    <Trophy size={14} className="text-green-600" />
                  ) : (
                    <Lock size={14} className="text-gray-400" />
                  )}
                  <p className={`text-xs font-bold ${milestone.isUnlocked ? 'text-green-700' : 'text-gray-600'}`}>
                    {milestone.title}
                  </p>
                </div>
                <span className="text-xs font-black text-black/60">
                  {milestone.currentProgress}/{milestone.threshold}
                </span>
              </div>
              {!milestone.isUnlocked && (
                <div className="w-full h-1.5 bg-black/5 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 transition-all duration-500"
                    style={{ width: `${Math.min((milestone.currentProgress / milestone.threshold) * 100, 100)}%` }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Achievements */}
      {progress.achievements.length > 0 && (
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-5 border border-amber-100">
          <div className="flex items-center gap-2 mb-4">
            <Award size={18} className="text-amber-600" />
            <h3 className="font-black text-sm text-black">{t.achievements}</h3>
            <span className="ml-auto text-xs font-bold text-amber-700">
              {progress.achievements.length}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {progress.achievements.map((achievement) => (
              <div key={achievement.id} className="bg-white/60 rounded-lg p-3 border border-white/60">
                <p className="text-xl mb-1">{achievement.icon}</p>
                <p className="text-xs font-bold text-black/80">{achievement.title}</p>
                <p className="text-[10px] text-black/50 mt-0.5">{achievement.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Unlocked Insights */}
      {progress.unlockedInsights.length > 0 && (
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-5 border border-purple-100">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={18} className="text-purple-600" />
            <h3 className="font-black text-sm text-black">Insights {t.unlocked}</h3>
          </div>
          <div className="space-y-2">
            {progress.unlockedInsights.map((insight) => (
              <div key={insight.id} className="bg-white/60 rounded-lg p-3 border border-white/60">
                <p className="text-xs font-bold text-purple-700 mb-1">{insight.title}</p>
                <p className="text-xs text-black/70">{insight.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressDashboard;
